/**
 * Pure helpers for the Acquisition tab: channel grouping, date presets and
 * step conversion / blocker maths (targets and thresholds from Charlie's
 * acquisition-funnel prototype).
 */

export type StepId = 'mql' | 'mql2' | 'onb' | 'ret';

/** One cohort's counts, summed from /activation-stats responses. */
export interface Counts {
  /** Browser-reported invite page views. */
  clicks: number;
  taps: number;
  mql: number;
  mql2: number;
  onb: number;
  retained: number;
  retEligible: number;
  /** First-message sign-ups per day (yyyy-mm-dd). */
  daily: Record<string, number>;
}

export const emptyCounts = (): Counts => ({
  clicks: 0,
  taps: 0,
  mql: 0,
  mql2: 0,
  onb: 0,
  retained: 0,
  retEligible: 0,
  daily: {},
});

export const addCounts = (a: Counts, b: Counts): Counts => {
  const daily = { ...a.daily };
  for (const [day, n] of Object.entries(b.daily)) {
    daily[day] = (daily[day] ?? 0) + n;
  }
  return {
    clicks: a.clicks + b.clicks,
    taps: a.taps + b.taps,
    mql: a.mql + b.mql,
    mql2: a.mql2 + b.mql2,
    onb: a.onb + b.onb,
    retained: a.retained + b.retained,
    retEligible: a.retEligible + b.retEligible,
    daily,
  };
};

export const CHANNELS = [
  { id: 'meta', label: 'Meta ads', color: '#2563eb' },
  { id: 'groups', label: 'Groups', color: '#0d9488' },
  { id: 'creators', label: 'Creator UGC', color: '#d97706' },
  { id: 'referrals', label: 'Referrals', color: '#7c3aed' },
  { id: 'organic', label: 'Organic & invites', color: '#64748b' },
  { id: 'other', label: 'Other', color: '#94a3b8' },
] as const;

export type ChannelId = (typeof CHANNELS)[number]['id'];

const SOURCE_CHANNEL: Record<string, ChannelId> = {
  'campaign:meta_ads_ea': 'meta',
  'campaign:meta_ads_pa': 'meta',
  'campaign:whatsapp_ad': 'meta',
  'campaign:revolut': 'groups',
  'campaign:mba': 'groups',
  'campaign:blue_badgers': 'groups',
  'campaign:fcfund': 'groups',
  'campaign:creators': 'creators',
  'referrer:ugc_creator': 'creators',
  personal_referral: 'referrals',
  dream_referral: 'referrals',
  'campaign:website': 'organic',
  organisation: 'organic',
  unattributed: 'organic',
};

/** Channel of an onboarding source key; new sources land in Other. */
export const channelOf = (source: string): ChannelId =>
  SOURCE_CHANNEL[source] ?? 'other';

export const isChannelId = (v: unknown): v is ChannelId =>
  typeof v === 'string' && CHANNELS.some((c) => c.id === v);

/**
 * Channel of a campaign code (or session): the channel set on the code in
 * Platform Stats → Campaign Codes wins; otherwise derived from the source.
 */
export const channelOfCode = (
  source: string,
  channel: string | null | undefined,
): ChannelId => (isChannelId(channel) ? channel : channelOf(source));

export type Targets = Record<StepId, number>;

/** Charlie's default conversion targets. */
export const DEFAULT_TARGETS: Targets = {
  mql: 0.02,
  mql2: 0.6,
  onb: 0.55,
  ret: 0.5,
};

export type StepStatus = 'good' | 'warn' | 'bad' | 'none';

export interface Step {
  id: StepId;
  /** Conversion from the previous stage; null when it can't be measured. */
  conv: number | null;
  /** People (or clicks) at the previous stage. */
  prev: number;
  target: number;
  ratio: number | null;
  status: StepStatus;
}

export const statusOf = (ratio: number | null): StepStatus =>
  ratio == null || !Number.isFinite(ratio)
    ? 'none'
    : ratio >= 1
      ? 'good'
      : ratio >= 0.75
        ? 'warn'
        : 'bad';

const rate = (num: number, den: number) => (den > 0 ? num / den : null);

/**
 * Step conversions. Click → MQL only counts when clicks were tracked for
 * the whole range and the cohort had some (click-to-WhatsApp ads skip
 * Dream's links, so they have none).
 */
export const stepsOf = (
  c: Counts,
  targets: Targets,
  clicksTracked: boolean,
): Step[] => {
  const raw: Array<[StepId, number | null, number]> = [
    [
      'mql',
      clicksTracked ? rate(c.mql, c.clicks) : null,
      clicksTracked ? c.clicks : 0,
    ],
    ['mql2', rate(c.mql2, c.mql), c.mql],
    ['onb', rate(c.onb, c.mql2), c.mql2],
    ['ret', rate(c.retained, c.retEligible), c.retEligible],
  ];
  return raw.map(([id, conv, prev]) => {
    const target = targets[id];
    const ratio = conv == null || target <= 0 ? null : conv / target;
    return { id, conv, prev, target, ratio, status: statusOf(ratio) };
  });
};

/** The step furthest below target, ignoring tiny samples (under 5). */
export const blockerOf = (steps: Step[]): Step | null =>
  steps
    .filter((s) => s.prev >= 5 && s.ratio != null && s.ratio < 1)
    .sort((a, b) => (a.ratio ?? 0) - (b.ratio ?? 0))[0] ?? null;

export const PRESETS = [
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['3d', 'Last 3 days'],
  ['7d', 'Last 7 days'],
  ['14d', 'Last 14 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['mtd', 'This month'],
  ['lm', 'Last month'],
  ['all', 'All time'],
] as const;

export type PresetId = (typeof PRESETS)[number][0];

export interface DateRange {
  from: string;
  to: string;
}

/** Before Dream's first onboarding sessions. */
export const ALL_TIME_FROM = '2025-01-01';

export const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const LAST_N: Partial<Record<PresetId, number>> = {
  '3d': 3,
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '90d': 90,
};

/** Inclusive yyyy-mm-dd range for a preset, counted back from `today`. */
export const presetRange = (id: PresetId, today: string): DateRange => {
  const n = LAST_N[id];
  if (n) return { from: addDays(today, -(n - 1)), to: today };
  const firstOfMonth = `${today.slice(0, 8)}01`;
  switch (id) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case 'mtd':
      return { from: firstOfMonth, to: today };
    case 'lm': {
      const last = addDays(firstOfMonth, -1);
      return { from: `${last.slice(0, 8)}01`, to: last };
    }
    default:
      return { from: ALL_TIME_FROM, to: today };
  }
};

export const rangeLength = (r: DateRange) =>
  Math.round((Date.parse(r.to) - Date.parse(r.from)) / 86_400_000) + 1;

/** The same-length range before (-1) or after (+1) this one. */
export const shiftRange = (r: DateRange, dir: 1 | -1): DateRange => {
  const n = rangeLength(r) * dir;
  return { from: addDays(r.from, n), to: addDays(r.to, n) };
};
